import { useState } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Select,
  Spin,
  Alert,
  Table,
  Empty,
  Tag,
  Progress,
} from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { ColumnsType } from 'antd/es/table';
import { useScreenViews, useFunnel } from '@/hooks/useScreenAnalytics';
import type { ScreenRanking, FunnelStep } from '@/api/screenAnalytics.api';

const { Title, Text } = Typography;

const LINE_COLORS = ['#1890ff', '#52c41a', '#fa8c16', '#722ed1', '#f5222d'];

export function ScreenAnalyticsPage() {
  const [period, setPeriod] = useState<'7' | '30' | '90'>('30');
  const days = parseInt(period, 10) as 7 | 30 | 90;

  const screenViews = useScreenViews(days);
  const funnel = useFunnel(days);

  if (screenViews.isLoading || funnel.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (screenViews.error || funnel.error || !screenViews.data || !funnel.data) {
    return <Alert message="데이터 로드 실패" type="error" showIcon />;
  }

  const { ranking, topScreens, dailyTrend } = screenViews.data;
  const funnelData = funnel.data;

  const rankingColumns: ColumnsType<ScreenRanking> = [
    {
      title: '순위',
      key: 'rank',
      width: 60,
      render: (_v, _r, i) => <strong>{i + 1}</strong>,
    },
    {
      title: '화면',
      dataIndex: 'screen',
      key: 'screen',
      render: (s: string) => <Tag color="blue">{s}</Tag>,
    },
    {
      title: 'PV (조회 수)',
      dataIndex: 'pv',
      key: 'pv',
      align: 'right',
      sorter: (a, b) => a.pv - b.pv,
      defaultSortOrder: 'descend',
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: 'UV (고유 사용자)',
      dataIndex: 'uv',
      key: 'uv',
      align: 'right',
      sorter: (a, b) => a.uv - b.uv,
      render: (v: number) => v.toLocaleString(),
    },
  ];

  // 퍼널: 첫 단계 대비 비율로 막대 길이 표시
  const baseUu = funnelData.steps[0]?.uu ?? 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            사용자 행동 분석
          </Title>
          <Text type="secondary">앱 화면 진입 추적 데이터 기반 분석</Text>
        </div>
        <Select
          value={period}
          onChange={(v) => setPeriod(v)}
          style={{ width: 120 }}
          options={[
            { value: '7', label: '최근 7일' },
            { value: '30', label: '최근 30일' },
            { value: '90', label: '최근 90일' },
          ]}
        />
      </div>

      {/* 매칭 요청 퍼널 */}
      <Card title={`매칭 요청 퍼널 (최근 ${period}일)`} style={{ borderRadius: 8, marginBottom: 24 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          앱을 사용한 유저 중 매칭 요청 화면에 도달하고, 실제로 요청을 생성한 비율
        </Text>
        {baseUu === 0 ? (
          <Empty description="기간 내 사용자 활동 데이터 없음" />
        ) : (
          <div>
            {funnelData.steps.map((step: FunnelStep, idx: number) => {
              const ratioOfBase = baseUu > 0 ? Math.round((step.uu / baseUu) * 1000) / 10 : 0;
              return (
                <div key={step.key} style={{ marginBottom: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div>
                      <Text strong style={{ fontSize: 14 }}>
                        {idx + 1}. {step.label}
                      </Text>
                      {step.conversionRate !== undefined && (
                        <Tag
                          color={step.conversionRate >= 0.5 ? 'green' : step.conversionRate >= 0.2 ? 'orange' : 'red'}
                          style={{ marginLeft: 10 }}
                        >
                          전 단계 대비 {(step.conversionRate * 100).toFixed(1)}%
                        </Tag>
                      )}
                    </div>
                    <Text strong>{step.uu.toLocaleString()}명</Text>
                  </div>
                  <Progress
                    percent={ratioOfBase}
                    showInfo={false}
                    strokeColor={LINE_COLORS[idx % LINE_COLORS.length]}
                  />
                </div>
              );
            })}
            <Text type="secondary" style={{ fontSize: 12 }}>
              * 매칭 요청 화면: {funnelData.matchRequestScreens.join(', ')}
              <br />* UU = 고유 사용자 수 (기간 내 1회 이상 도달)
            </Text>
          </div>
        )}
      </Card>

      {/* 페이지별 PV/UV 순위 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={`페이지별 방문 순위 (최근 ${period}일)`} style={{ borderRadius: 8 }}>
            {ranking.length === 0 ? (
              <Empty description="수집된 데이터 없음" />
            ) : (
              <Table<ScreenRanking>
                columns={rankingColumns}
                dataSource={ranking}
                rowKey="screen"
                pagination={{ pageSize: 15, size: 'small' }}
                size="small"
              />
            )}
          </Card>
        </Col>

        {/* 상위 5개 페이지 일별 추이 */}
        <Col xs={24} lg={12}>
          <Card title={`상위 5개 페이지 일별 추이 (최근 ${period}일)`} style={{ borderRadius: 8 }}>
            {dailyTrend.length === 0 || topScreens.length === 0 ? (
              <Empty description="수집된 데이터 없음" />
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    interval={period === '7' ? 0 : Math.floor(dailyTrend.length / 7)}
                  />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip />
                  <Legend />
                  {topScreens.map((s, i) => (
                    <Line
                      key={s}
                      type="monotone"
                      dataKey={s}
                      name={s}
                      stroke={LINE_COLORS[i % LINE_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
